import React from 'react';
import { Clock, Info, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const GptPlusFleetNotice = ({ notice }) => notice ? (
  <div role="note" className="mb-4 flex gap-2 rounded-lg border border-accent-100 bg-accent-50 px-3 py-2 text-sm text-accent-800">
    <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
    <p className="break-words">{notice}</p>
  </div>
) : null;

const GptPlusNodeCard = ({ node, onUse }) => {
  const { t, i18n } = useTranslation();
  const stopped = node.status !== 'online';
  const statusKey = stopped ? 'unavailable' : node.color === 'yellow' ? 'busy' : node.color === 'green' ? 'idle' : 'available';
  const statusLabel = statusKey === 'idle' ? 'idleRecommended' : statusKey === 'busy' ? 'busyAvailable' : statusKey;
  // 已合并进状态标签的文案不再重复占一行；其他后端说明照常展示。
  const statusWords = statusKey === 'idle' ? ['空闲', '推荐', '可用'] : statusKey === 'busy' ? ['繁忙', '可用'] : [];
  const description = (node.description || '').split(/[·|/]/).map(part => part.trim()).filter(part => part && !statusWords.includes(part)).join(' · ');
  const statusStyles = {
    available: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    idle: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    busy: 'border-amber-200 bg-amber-50 text-amber-700',
    unavailable: 'border-ink-100 bg-cream-100 text-ink-500',
  };
  const iqKey = node.isFullPower === true ? 'fullPower' : node.isFullPower === false ? 'degraded' : 'unknownPower';
  const formatDate = value => new Date(value).toLocaleString(i18n.language, {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', year: 'numeric',
  });
  const featureKeys = { deep_research: 'deepResearch', file_upload: 'fileUpload', paste_text_to_file: 'pasteText', image_gen: 'imageGeneration' };
  const limits = node.limits || [];

  return (
    <article aria-label={t('aiFleet.plusNodes.nodeLabel', { id: node.id })}
      className="fleet-node-card fleet-node-card-plus relative flex h-full min-w-0 flex-col overflow-hidden rounded-xl border bg-white transition-colors sm:min-h-[248px]">
      <div className="absolute left-2.5 top-2.5 z-10" aria-hidden="true">
        <span className={`block h-2 w-2 rounded-full ${stopped ? 'bg-red-500' : 'bg-green-500'}`} />
      </div>
      <div className="fleet-node-card-top relative flex items-center justify-between gap-2">
        <div className="flex shrink-0 items-center">
          <img src="/images/gpt-main.jpg" alt="" className="h-8 w-8 rounded-lg object-cover" />
        </div>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-1">
          <span className="rounded bg-accent-500 px-1.5 py-0.5 text-xs font-bold text-white">{node.label}</span>
          {node.isFullPower !== null && <span className={`rounded px-1.5 py-0.5 text-xs ${node.isFullPower ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'}`}>{t(`aiFleet.plusNodes.${iqKey}`)}</span>}
          <span className={`rounded border px-1.5 py-0.5 text-[10px] leading-4 ${statusStyles[statusKey]}`}>
            {t(`aiFleet.plusNodes.${statusLabel}`)}
          </span>
        </div>
      </div>
      <div className="fleet-node-card-body min-w-0">
      <div className="mb-1 text-center text-[10px] font-medium tracking-wide text-accent-600">{t('aiFleet.plusNodes.newLine')}</div>
      <h4 className="mb-3 break-all text-center font-mono text-sm font-semibold text-ink-900 sm:text-base">{node.id}</h4>
      {description && <p className="mb-3 break-words text-center text-xs leading-5 text-ink-500">{description}</p>}
      {node.count !== null && <div className="mb-3 flex flex-wrap items-center justify-center gap-1 text-xs text-ink-500">
        <Users className="h-3.5 w-3.5" />
        <span>{t('aiFleet.plusNodes.userCount', { count: node.count })}</span>
      </div>}
      {limits.length > 0 && <div aria-label={t('aiFleet.plusNodes.quotaDetails')} className="mb-3 flex flex-wrap justify-center gap-1.5">
        {limits.map((limit, index) => {
          const feature = featureKeys[limit.feature] ? t(`aiFleet.plusNodes.features.${featureKeys[limit.feature]}`) : limit.feature;
          const remaining = limit.remaining !== null ? t('aiFleet.plusNodes.remaining', { count: limit.remaining }) : '';
          const timing = [
            limit.resetAt && t('aiFleet.plusNodes.resetAt', { time: formatDate(limit.resetAt) }),
            node.metadataUpdatedAt && t('aiFleet.plusNodes.updatedAt', { time: formatDate(node.metadataUpdatedAt) }),
          ].filter(Boolean).join('\n');
          return <span key={`${limit.feature}-${index}`} tabIndex={0} title={timing || undefined}
            aria-label={[feature, remaining, timing].filter(Boolean).join('，')}
            className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] leading-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 ${limit.remaining === 0 ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-ink-100 bg-cream-50 text-ink-600'}`}>
            <span className="min-w-0 break-words">{feature}</span>
            {limit.remaining !== null && <span className="font-semibold tabular-nums">{limit.remaining}</span>}
          </span>;
        })}
      </div>}
      {node.limitDescription && <p className="mb-3 break-words rounded-lg bg-amber-50 px-2 py-1.5 text-xs leading-5 text-amber-800">{node.limitDescription}</p>}
      {node.recoverySeconds > 0 && (
        <p className="mb-3 flex items-start gap-1 text-xs leading-5 text-ink-500">
          <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          {t('aiFleet.plusNodes.recovery', { minutes: Math.ceil(node.recoverySeconds / 60) })}
        </p>
      )}
      <button type="button" onClick={() => onUse(node)} disabled={stopped}
        aria-label={t(stopped ? 'aiFleet.plusNodes.stoppedLabel' : 'aiFleet.plusNodes.useLabel', { id: node.id })}
        className="mt-auto min-h-[40px] w-full rounded-md border-2 border-accent-500 bg-accent-500 px-2 py-2.5 text-xs font-bold text-white transition-colors enabled:hover:border-accent-600 enabled:hover:bg-accent-600 enabled:hover:shadow-md enabled:active:bg-accent-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-cream-100 disabled:text-ink-400 sm:rounded-lg sm:text-sm">
        {t(stopped ? 'aiFleet.nodeInfo.stopped' : 'aiFleet.nodeInfo.useNow')}
      </button>
      </div>
    </article>
  );
};

export default GptPlusNodeCard;
